import { Euler, Mesh, MeshBasicMaterial, Object3D, PlaneGeometry, Quaternion, Vector2, Vector3 } from 'three';
import { ActionControllerEventName, Input, Tween, Viewer, ViewerModule } from '@verybigthings/vbt-webvr-framework';
import { InputManager } from '@verybigthings/vbt-webvr-framework/build/main/ts/utils/InputManager';
import { WINDOW_HEADER_TEXT_BACKGROUND_COLOR } from '../../styleConfig';
import { PickingControllerEvents } from '@verybigthings/vbt-webvr-framework/build/main/ts/controls/PickingController';
import { PreparationPickingObjects } from "../../utils/PreparationPickingObjects";
export default class LabelModule extends ViewerModule {
    constructor(model, table, uiContainer, level) {
        super();
        this.level = level;
        window.addEventListener('keyup', (event) => {
            if (!this.activeInput)
                return;
            if (event.key.length === 1 && event.key.match(/^[A-Za-z]+$/))
                this.activeInput.addSymbol(event.key);
            if (event.key === 'Enter')
                this.activeInput.setEnter();
            if (event.key === 'Backspace')
                this.activeInput.deleteSymbol();
        });
        this.uiObject.name = 'LabelModule';
        this.model = model;
        this.model.material = new MeshBasicMaterial();
        this.bottleLabel = table.getObjectByName('result_jar_label');
        this.bottleLabel.visible = false;
        this.table = table;
        this.defaultPosition = model.position.clone();
        // this.addObject(this.uiContainer = uiContainer);
        this.uiContainer = uiContainer;
        this.initInput();
        this.model.geometry.computeBoundingBox();
        const sizeLabel = this.model.geometry.boundingBox.getSize(new Vector3());
        this.labelBorder = new Mesh(new PlaneGeometry(sizeLabel.x + 0.01, sizeLabel.z + 0.01), new MeshBasicMaterial({ color: WINDOW_HEADER_TEXT_BACKGROUND_COLOR }));
        this.model.add(this.labelBorder);
        this.labelBorder.rotateX(-Math.PI / 2);
    }
    flyOnTable() {
        this.rotateTo(new Euler());
        this.setScale(1, 1000);
        this.flyTo(this.defaultPosition, 1000);
    }
    visibilityShadow(visibility = true) {
        const pPlane = this.model.getObjectByName('pPlane');
        if (pPlane)
            pPlane.visible = visibility;
    }
    flyTo(pos, time = 1000, onComplete) {
        new Tween(this.model.position)
            .to(pos.clone(), time)
            .easing(TWEEN.Easing.Linear.None)
            .onComplete(() => onComplete && onComplete())
            .start();
    }
    setScale(scaleVal, time = 1000) {
        new Tween(this.model.scale)
            .to({ x: scaleVal, y: scaleVal, z: scaleVal }, time)
            .easing(TWEEN.Easing.Linear.None)
            .start();
    }
    rotateTo(newEuler, time = 600, delay = 0, onComplete) {
        const oldQuaternion = this.model.quaternion.clone();
        const newQuaternion = new Quaternion().setFromEuler(newEuler);
        new Tween({ alpha: 0 })
            .to({ alpha: 1 }, time)
            .easing(TWEEN.Easing.Linear.None)
            .onUpdate((alpha) => {
            const quat = oldQuaternion.clone().slerp(newQuaternion.clone(), alpha);
            this.model.quaternion.copy(quat);
        })
            .onComplete(() => onComplete && onComplete())
            .delay(delay)
            .start();
    }
    initWaitSelectForSignature() {
        PreparationPickingObjects.saveObjectState(this.model);
        this.actionController.once(ActionControllerEventName.click, this.model, () => {
            this.toPrintLabelText(() => {
                this.input.uiObject.visible = true;
                InputManager.setInput(this.input);
                const keyboardPositioning = () => {
                    this.activeInput = this.input;
                    this.addObject(InputManager.keyboard);
                    if (Viewer.controls.zController) {
                        InputManager.keyboard.uiObject.position.set(0, -2.5, 3);
                        InputManager.keyboard.uiObject.scale.setScalar(0.3);
                    }
                    else {
                        InputManager.keyboard.uiObject.position.set(0, -1, 4);
                        InputManager.keyboard.uiObject.scale.set(0.2, 0.2, 0.2);
                    }
                };
                keyboardPositioning();
                this.input.on('onInputClicked', () => setTimeout(keyboardPositioning));
                this.input.on('entered', () => {
                    InputManager.blurInput();
                    this.activeInput = null;
                    if (this.input.getText().toLowerCase() === 'nacl') {
                        this.input.text.setText('NaCl');
                        this.actionController.off(ActionControllerEventName.click, this.input.uiObject);
                        this.fire('successfully_signed');
                    }
                    else {
                        this.fire('wrong_signed');
                    }
                });
            });
        });
    }
    initWaitSelectForApply(target) {
        this.actionController.once(ActionControllerEventName.click, this.model, () => {
            if (Viewer.controls.zController)
                return;
            const pointerLabelWrap = new Object3D();
            pointerLabelWrap.add(this.model);
            this.model.position.set(0, 0.2, 0);
            this.model.rotation.set(1.5, 0, -5.64);
            Viewer.pointerController.setObject(pointerLabelWrap);
            this.visibilityShadow(false);
            this.actionController.once(ActionControllerEventName.click, target, () => {
                Viewer.pointerController.setObject(null);
                const bottleLabel = this.table.getObjectByName('result_jar_label');
                bottleLabel.position.x = 0.16;
                bottleLabel.position.z = -0.85;
                bottleLabel.rotation.y = -0.34;
                // @ts-ignore
                bottleLabel.material = this.input.text.uiObject.children[0].material;
                this.bottleLabel.visible = true;
                bottleLabel.material.map.offset.set(0.05, 0.1);
                bottleLabel.material.map.repeat.set(34, 0.7);
                this.fire('label_applied');
            });
        });
        if (Viewer.controls.zController && this.level.lobby.app.zSpacePickingController) {
            this.level.lobby.app.zSpacePickingController.on(PickingControllerEvents.PICKED, this.model, () => {
                if (!this.level.interaction)
                    return;
                Viewer.scene.add(this.model);
                this.visibilityShadow(false);
                this.level.lobby.app.zSpacePickingController.once(PickingControllerEvents.RELEASED, this.model, () => {
                    const distanceToResultJar = this.model.getWorldPosition(new Vector3()).distanceTo(target.getWorldPosition(new Vector3()));
                    if (distanceToResultJar < 1) {
                        this.actionController.off(ActionControllerEventName.click, this.model);
                        this.level.lobby.app.zSpacePickingController.off(PickingControllerEvents.PICKED, this.model);
                        const bottleLabel = this.table.getObjectByName('result_jar_label');
                        bottleLabel.position.x = 0.16;
                        bottleLabel.position.z = -0.85;
                        bottleLabel.rotation.y = -0.34;
                        // @ts-ignore
                        bottleLabel.material = this.input.text.uiObject.children[0].material;
                        this.bottleLabel.visible = true;
                        bottleLabel.material.map.offset.set(0.05, 0.1);
                        bottleLabel.material.map.repeat.set(34, 0.7);
                        this.fire('label_applied');
                    }
                    else {
                        PreparationPickingObjects.flyObjectToPrevState(this.model);
                    }
                });
            });
        }
    }
    toPrintLabelText(onComplete) {
        const toX_Angle = (!Viewer.controls.zController) ? Math.PI / 2 : 1.37;
        const toZ_Position = (!Viewer.controls.zController) ? -4.5 : 2.7;
        this.rotateTo(new Euler(toX_Angle, 0, 0));
        this.setScale(5, 1000);
        this.flyTo(new Vector3(0, 7, toZ_Position), 1000, onComplete);
    }
    initInput() {
        const inputOptions = {
            size: new Vector2(1.7 * 2.18 / 1.3, 1.7 / 1.1),
            pxSize: new Vector2(64 * 2.18 / 1.3, 64 / 1.1),
            background: 0xffffff,
            bordRadius: 0.15,
            maxLength: 6,
            text: {
                autoWrappingHorizontal: true,
                autoWrapping: false,
                style: { size: '38px', color: 'black' },
                lineHeight: 36,
            }
        };
        this.input = new Input(inputOptions);
        this.model.add(this.input.uiObject);
        this.input.uiObject.position.set(0.1, 0.02, 0);
        this.input.uiObject.rotateX(-Math.PI / 2);
        this.input.uiObject.scale.set(0.2, 0.2, 0.2);
        this.input.uiObject.visible = false;
    }
    initDragAndDropLabel(goalModel) { }
    hideModel() {
        this.model.visible = false;
    }
}
